import { trackEvent } from './monitoring';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

let initData: string | null = null;
let onAiProvider: ((provider: string, endpoint: string) => void) | null = null;

export function setInitData(data: string) {
  initData = data;
}

export function setAiProviderCallback(cb: (provider: string, endpoint: string) => void) {
  onAiProvider = cb;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Ошибка сети' }));
    // Обогащаем Error метаданными — чтобы callers могли различать 4xx ответы
    // (например, 409 duplicate с existing-телом). Старое поведение по message
    // сохраняется.
    const err: Error & { status?: number; body?: any } = new Error(
      error.message || error.error || `HTTP ${response.status}`,
    );
    err.status = response.status;
    err.body = error;
    throw err;
  }

  const data = await response.json();
  if (data && typeof data === 'object' && '_ai_provider' in data && data._ai_provider !== 'fallback') {
    const provider = data._ai_provider as string;
    // Трекаем каждый AI-ответ независимо от настройки тоста
    const { _ai_provider, ...responsePayload } = data as Record<string, unknown>;
    const requestBody = typeof options.body === 'string' ? options.body.slice(0, 1000) : undefined;
    trackEvent('ai_response', { provider, endpoint: path, request: requestBody, response: JSON.stringify(responsePayload).slice(0, 1000) });
    if (onAiProvider) {
      onAiProvider(provider, path);
    }
  }
  return data as T;
}

async function requestBlob(
  path: string,
  options: RequestInit = {},
): Promise<Blob> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.blob();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  // TTS fallback — возвращает Blob (audio/mpeg)
  tts: (text: string, lang: string = 'en') =>
    requestBlob('/ai/tts', { method: 'POST', body: JSON.stringify({ text, lang }) }),
};
